import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Checkbox,
  Chip,
  Tooltip,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  MenuItem,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  fetchParty,
  PartyUpdate,
  deleteParty,
  PartyCreate,
  ApprovePartyapi,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";

// ============================================================
// ✅✅✅ FIX #9: FIELD_GUIDE — every field's DEMO + GUIDE (ENGLISH)
// - "demo"  → shown inside the field as placeholder (how to fill)
// - "guide" → shown in the popup telling the correct format
// (Kept at module level so ContactPersonsSection can also use it)
// ============================================================
const FIELD_GUIDE = {
  company_name: {
    label: "Company Name",
    demo: "e.g. Acme Industries Pvt. Ltd.",
    guide: "Enter the debtor company's full name",
  },
  name: {
    label: "Contact Person",
    demo: "e.g. Rakesh Sharma",
    guide: "Enter the main contact person's full name",
  },
  email: {
    label: "Email",
    demo: "e.g. name@company.com",
    guide: "Enter a valid email — format: name@domain.com",
  },
  contact: {
    label: "Phone No",
    demo: "e.g. 9876543210",
    guide: "Enter exactly 10 digits — without +91 or spaces",
  },
  gst: {
    label: "GST No",
    demo: "e.g. 27ABCDE1234F1Z5",
    guide:
      "Enter the 15-character GSTIN — 2 digits + 5 letters + 4 digits + 1 letter + 1 digit + Z + 1 character",
  },
  pan: {
    label: "PAN No",
    demo: "e.g. ABCDE1234F",
    guide: "Enter 10 characters — 5 letters + 4 digits + 1 letter",
  },
  address: {
    label: "Address",
    demo: "e.g. 12, MG Road, Indore (M.P.) 452001",
    guide: "Enter the full address with city and PIN code",
  },
  // For Contact Persons section
  cp_name: {
    label: "Person Name",
    demo: "e.g. Rakesh Sharma",
    guide: "Enter the contact person's full name",
  },
  cp_no: {
    label: "Person Contact No",
    demo: "e.g. 9876543210",
    guide: "Enter exactly 10 digits — without +91 or spaces",
  },
  cp_designation: {
    label: "Designation",
    demo: "e.g. Purchase Manager",
    guide: "Enter the person's post / designation",
  },
};

// ✅ Contact Persons Section - COMPONENT KE BAHAR (no re-render issue)
const ContactPersonsSection = ({
  contactPersons = [],
  onAdd,
  onRemove,
  onChange,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          Contact Persons
        </Typography>
        <Tooltip title="Add New Contact Person" arrow>
          <IconButton
            size="small"
            onClick={onAdd}
            color="primary"
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              width: 28,
              height: 28,
              "&:hover": { bgcolor: "primary.main", color: "white" },
            }}
          >
            <AddIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {contactPersons.map((person, idx) => (
        <Grid
          container
          spacing={1}
          key={idx}
          sx={{
            mb: 1.5,
            alignItems: "center",
            bgcolor: (t) =>
              idx % 2 === 0
                ? t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.04)
                  : alpha(t.palette.common.black, 0.02)
                : "transparent",
            p: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Title/Prefix field */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Title"
              fullWidth
              size="small"
              value={person.title || ""}
              onChange={(e) => onChange(idx, "title", e.target.value)}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      zIndex: 9999,
                    },
                  },
                },
              }}
            >
              <MenuItem value="Mr.">Mr.</MenuItem>
              <MenuItem value="Mrs.">Mrs.</MenuItem>
              <MenuItem value="Ms.">Ms.</MenuItem>
              <MenuItem value="Dr.">Dr.</MenuItem>
              <MenuItem value="Prof.">Prof.</MenuItem>
            </TextField>
          </Grid>

          {/* Name field */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              autoComplete="off"
              // ✅ FIX #9: DEMO placeholder — shows how to fill
              placeholder={FIELD_GUIDE.cp_name.demo}
              value={person.contact_name || ""}
              onChange={(e) => onChange(idx, "contact_name", e.target.value)}
            />
          </Grid>

          {/* Contact No field */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Contact No"
              fullWidth
              size="small"
              autoComplete="off"
              // ✅ FIX #9: DEMO placeholder
              placeholder={FIELD_GUIDE.cp_no.demo}
              value={person.contact_no || ""}
              onChange={(e) => {
                let value = e.target.value.replace(/[^0-9]/g, "");
                if (value.length > 10) value = value.slice(0, 10);
                onChange(idx, "contact_no", value);
              }}
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
            />
          </Grid>

          {/* Designation field */}
          <Grid item xs={10} sm={6} md={3}>
            <TextField
              label="Designation"
              fullWidth
              size="small"
              autoComplete="off"
              // ✅ FIX #9: DEMO placeholder
              placeholder={FIELD_GUIDE.cp_designation.demo}
              value={person.designation || ""}
              onChange={(e) => onChange(idx, "designation", e.target.value)}
            />
          </Grid>

          {/* Delete button */}
          <Grid item xs={2} sm={6} md={1} sx={{ textAlign: "center" }}>
            {contactPersons.length > 1 && (
              <Tooltip title="Remove" arrow>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemove(idx)}
                  sx={{
                    border: "1px solid",
                    borderColor: "error.light",
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.error.main, 0.15)
                          : "#ffebee",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

const PartyManagementTable = () => {
  const [Partys, setPartys] = useState([]);
  const [filter, setFilter] = useState({ name: "" });

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewParty, setViewParty] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [PartyToDelete, setPartyToDelete] = useState(null);

  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [partyToApprove, setPartyToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);

  const [validationPopup, setValidationPopup] = useState({
    open: false,
    title: "",
    problems: [],
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [errors, setErrors] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedParty, setExpandedParty] = useState(null);

  // ============================================================
  // ✅✅✅ FIX #1: DARK MODE STICKY FIX — 100% SOLID COLORS
  // (alpha() NAHI use karna — translucent bg ki wajah se hi
  //  dark mode me peeche ka content dikh raha tha)
  // ============================================================
  const isDark = theme.palette.mode === "dark";
  const headerStickyBg = isDark ? "#252b38" : "#f0f4ff";
  const zebraBg = (i) =>
    isDark
      ? i % 2 === 0
        ? "#20242e" // even row - dark
        : "#1a1d26" // odd row - dark
      : i % 2 === 0
      ? "#f5f8ff" // even row - light
      : "#ffffff"; // odd row - light
  const rowHoverBg = isDark ? "#2c3242" : "#e8efff";
  // ============================================================

  // ✅ Empty contact person template
  const getEmptyContactPerson = () => ({
    title: "",
    contact_name: "",
    contact_no: "",
    designation: "",
  });

  const getEmptyFormData = () => ({
    name: "",
    email: "",
    address: "",
    contact: "",
    gst: "",
    pan: "",
    state: "",
    company_name: "",
    contact_persons: [getEmptyContactPerson()],
    created_by: localStorage.getItem("user_id"),
  });

  const [formData, setFormData] = useState(getEmptyFormData());

  // ✅ Contact Person Handlers
  const handleAddContactPerson = () => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: [
        ...(prev.contact_persons || []),
        getEmptyContactPerson(),
      ],
    }));
  };

  const handleRemoveContactPerson = (index) => {
    setFormData((prev) => ({
      ...prev,
      contact_persons: prev.contact_persons.filter((_, i) => i !== index),
    }));
  };

  const handleContactPersonChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.contact_persons || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, contact_persons: updated };
    });
  };

  useEffect(() => {
    fetchPartys(1, false);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchPartys(1, false);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter.name]);

  const fetchPartys = async (pageNumber = 1, append = false) => {
    try {
      const res = await fetchParty(pageNumber, { name: filter.name });
      const newData = res?.results || res || [];
      if (append) {
        setPartys((prev) => [...prev, ...newData]);
      } else {
        setPartys(newData);
      }
      setHasMore(!!res?.next);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const fetchMoreData = () => {
    if (hasMore) {
      setPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchPartys(nextPage, true);
        return nextPage;
      });
    }
  };

  const companyExists = (companyName, excludeId = null) => {
    const list = Array.isArray(Partys) ? Partys : [];
    const target = (companyName || "").trim().toLowerCase();
    return list.some(
      (p) =>
        (p?.company_name || "").trim().toLowerCase() === target &&
        (excludeId ? p.id !== excludeId : true)
    );
  };

  // ============================================================
  // ✅✅✅ FIX #10: validateForm — checks the whole form at once and
  // reports for EVERY mistake: WHAT is wrong + HOW to fix + DEMO
  // (Old validateField() stays AS IT IS — this is a layer above it)
  // ============================================================
  const validateForm = (mode = "add") => {
    const problems = [];
    const alreadyAdded = (key) => problems.some((p) => p.key === key);

    // 1) Required fields must not be empty
    //    (matches the * stars shown in the dialogs)
    const requiredFields =
      mode === "add" ? ["company_name"] : ["company_name", "contact"];

    requiredFields.forEach((field) => {
      if (!formData[field] || String(formData[field]).trim() === "") {
        problems.push({
          key: field,
          label: FIELD_GUIDE[field].label,
          issue: "This field is required (currently empty)",
          fix: FIELD_GUIDE[field].guide,
          demo: FIELD_GUIDE[field].demo,
        });
      }
    });

    // 2) Format check (only when the field has a value)
    ["email", "contact", "pan", "gst"].forEach((field) => {
      if (alreadyAdded(field)) return;
      const msg = validateField(field, formData[field]);
      if (msg) {
        problems.push({
          key: field,
          label: FIELD_GUIDE[field].label,
          issue: msg,
          fix: FIELD_GUIDE[field].guide,
          demo: FIELD_GUIDE[field].demo,
        });
      }
    });

    // 3) Contact persons' phone check (if filled, it must be 10 digits)
    (formData.contact_persons || []).forEach((person, i) => {
      if (person.contact_no && !/^\d{10}$/.test(person.contact_no)) {
        problems.push({
          key: `cp_no_${i}`,
          label: `Contact Person ${i + 1} — Contact No`,
          issue: "Phone number must be exactly 10 digits",
          fix: FIELD_GUIDE.cp_no.guide,
          demo: FIELD_GUIDE.cp_no.demo,
        });
      }
    });

    return problems;
  };

  // ✅✅✅ FIX #10: if there are problems → show POPUP + inline red errors
  // Returns: true = all good, false = errors found (popup opened)
  const showValidationPopupIfAny = (problems, title) => {
    if (problems.length === 0) return true;

    // Keep the old inline red-error behaviour as well
    const newErrors = {};
    problems.forEach((p) => {
      if (p.key && !String(p.key).startsWith("cp_") && !newErrors[p.key]) {
        newErrors[p.key] = p.issue;
      }
    });
    setErrors(newErrors);

    setValidationPopup({ open: true, title, problems });
    toast.error(
      `Form has ${problems.length} error(s) — see the popup for how to fix them`
    );
    return false;
  };

  const handleAddParty = async () => {
    // ✅✅✅ FIX #10: OLD checked only "company_name" — NOW full check + popup
    const problems = validateForm("add");

    if (companyExists(formData.company_name)) {
      problems.push({
        key: "company_name",
        label: FIELD_GUIDE.company_name.label,
        issue: "Debtor company already exists",
        fix: "This company name is already in the list — please use a slightly different name",
        demo: FIELD_GUIDE.company_name.demo,
      });
    }

    if (!showValidationPopupIfAny(problems, "Add Debtor")) return;

    try {
      const cleanedContactPersons = (formData.contact_persons || []).filter(
        (p) =>
          p.contact_name?.trim() ||
          p.contact_no?.trim() ||
          p.designation?.trim()
      );

      await PartyCreate({
        ...formData,
        company_name: formData.company_name.trim(),
        contact_persons: cleanedContactPersons,
      });
      fetchPartys();
      resetForm();
      setOpenAddModal(false);
      toast.success("Debtor created successfully");
    } catch (error) {
      const serverMsg =
        error?.response?.data?.company_name?.[0] ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.detail ||
        error?.message;
      if (serverMsg) {
        toast.info(serverMsg);
        setErrors((prev) => ({ ...prev, company_name: serverMsg }));
      } else {
        toast.error("Failed to create Debtor");
      }
    }
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setErrors({});
  };

  const handleDelete = async (party) => {
    try {
      await deleteParty(party.id);
      const updated = Partys.filter((v) => v.id !== party.id);
      setPartys(updated);
      toast.success("Debtor deleted successfully");
    } catch (error) {
      toast.error("Failed to delete Debtor");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleEdit = (party, index) => {
    setFormData({
      ...party,
      contact_persons:
        party.contact_persons?.length > 0
          ? party.contact_persons
          : [getEmptyContactPerson()],
    });
    setPartyToApprove(null);
    setEditIndex(index);
    setOpenEditModal(true);
  };

  const handleViewParty = (party) => {
    setViewParty(party);
    setOpenViewModal(true);
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "email":
        if (value && !/^\S+@\S+\.\S+$/.test(value))
          error = "Invalid email (e.g. name@example.com)";
        break;
      case "contact":
        if (value && !/^\d{10}$/.test(value))
          error = "Invalid phone number (e.g. 9876543210)";
        break;
      case "pan":
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value.toUpperCase()))
          error = "Invalid PAN (e.g. ABCDE1234F)";
        break;
      case "gst":
        if (
          value &&
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
            value.toUpperCase()
          )
        )
          error = "Invalid GST (e.g. 27ABCDE1234F1Z5)";
        break;
      default:
        break;
    }
    return error;
  };

  const handleUpdateParty = async (id) => {
    // ✅✅✅ FIX #10: OLD checked only "company_name" — NOW full check + popup
    const problems = validateForm("edit");

    if (!id) {
      toast.error("Debtor ID not found");
      return;
    }

    if (companyExists(formData.company_name, id)) {
      problems.push({
        key: "company_name",
        label: FIELD_GUIDE.company_name.label,
        issue: "Debtor company already exists",
        fix: "Another debtor already uses this company name — please use a slightly different name",
        demo: FIELD_GUIDE.company_name.demo,
      });
    }

    if (!showValidationPopupIfAny(problems, "Update Debtor")) return;

    try {
      const cleanedContactPersons = (formData.contact_persons || []).filter(
        (p) =>
          p.contact_name?.trim() ||
          p.contact_no?.trim() ||
          p.designation?.trim()
      );

      const res = await PartyUpdate(id, {
        ...formData,
        company_name: formData.company_name.trim(),
        contact_persons: cleanedContactPersons,
      });
      if (res) {
        toast.success("Debtor updated successfully ✅");
        await fetchPartys();
        setOpenEditModal(false);
        setEditIndex(null);
        setFormData(getEmptyFormData());
        setErrors({});
        setPartyToApprove(null);
      } else {
        toast.error("Failed to update Debtor");
      }
    } catch (error) {
      const serverMsg =
        error?.response?.data?.company_name?.[0] ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.detail ||
        error?.message;
      if (serverMsg) {
        toast.info(serverMsg);
        setErrors((prev) => ({ ...prev, company_name: serverMsg }));
      } else {
        toast.error("Error updating Debtor");
      }
    }
  };

  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = parseInt(localStorage.getItem("user_id") ?? "", 10);
    if (!Number.isInteger(approvedById)) {
      toast.error("Approver ID invalid or missing");
      return;
    }

    try {
      const partyId = partyToApprove.id;
      const updated = await ApprovePartyapi(
        partyId,
        approvedById,
        actionType,
        remarkInput
      );

      setPartys((prev) =>
        prev.map((item) =>
          item.id === partyId ? { ...item, ...updated } : item
        )
      );

      const msg =
        actionType === "Approved"
          ? "Debtor approved successfully!"
          : "Debtor rejected successfully!";
      toast.success(msg);

      setOpenApproveRejectModal(false);
      setOpenEditModal(false);
      setRemarkInput("");
      setActionType(null);
      setPartyToApprove(null);
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error("Failed to update debtor approval.");
    }
  };

  function formatOnlyDate(dateTimeString, format = "dd-mm-yyyy") {
    if (!dateTimeString) return "-";
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    if (format === "dd-mm-yyyy") return `${day}-${month}-${year}`;
    if (format === "yyyy-mm-dd") return `${year}-${month}-${day}`;
    throw new Error("Unsupported format. Use 'dd-mm-yyyy' or 'yyyy-mm-dd'.");
  }

  const userRole = localStorage.getItem("role");

  // ✅ Status border color helper
  const getStatusBorderColor = (status) => {
    if (status === "Approved") return "#4caf50";
    if (status === "Rejected") return "#f44336";
    return "#ff9800";
  };

  // Mobile Card Component
  const MobilePartyCard = ({ party, index }) => {
    const isExpanded = expandedParty === party.id;

    return (
      <Box key={party.id}>
        <Box
          onClick={() =>
            setExpandedParty(expandedParty === party.id ? null : party.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: (t) =>
              index % 2 === 0
                ? t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.03)
                  : alpha(t.palette.primary.main, 0.02)
                : "background.paper",
            "&:active": { bgcolor: "action.selected" },
            borderLeft: `4px solid ${getStatusBorderColor(party.status)}`,
            transition: "background-color 0.15s ease",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "text.disabled",
                  minWidth: 25,
                }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  flex: 1,
                  color: "text.primary",
                }}
              >
                {party.company_name}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{party.contact}</span>
              <span>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {truncateText(party.email, 15)}
              </span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.05)
                  : alpha(t.palette.primary.main, 0.04),
              borderLeft: `4px solid ${getStatusBorderColor(party.status)}`,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={party.status || "Pending"}
                  color={
                    party.status === "Approved"
                      ? "success"
                      : party.status === "Rejected"
                      ? "error"
                      : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  GST NO
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  {party.gst || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  PAN
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                >
                  {party.pan || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  CONTACT PERSON
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  {party.name || "-"}
                </Typography>
              </Box>
            </Box>

            {party.address && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    mb: 0.3,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  ADDRESS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                  }}
                >
                  {party.address}
                </Typography>
              </Box>
            )}

            {party.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.disabled",
                    mb: 0.3,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                  }}
                >
                  {party.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "divider" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(party, index);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha("#4caf50", 0.15)
                        : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha("#4caf50", 0.25)
                          : "#c8e6c9",
                    },
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color: "success.main",
                    }}
                  />
                </IconButton>

                {/* Delete Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setPartyToDelete(party);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha("#f44336", 0.15)
                        : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha("#f44336", 0.25)
                          : "#ffcdd2",
                    },
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1rem",
                      color: "error.main",
                    }}
                  />
                </IconButton>

                {/* View Button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewParty(party);
                  }}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.primary.main, 0.15)
                        : "#e3f2fd",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.25)
                          : "#bbdefb",
                    },
                  }}
                >
                  <VisibilityIcon
                    sx={{ fontSize: "1rem", color: "primary.main" }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* FILTERS BAR */}
      <Box
        sx={{
          pt: 1,
          pb: 1,
          px: 1,
          gap: 1,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 1100,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          mb: 1,
          mt: { xs: 4, sm: 0 },
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "0 1px 8px rgba(0,0,0,0.5)"
              : "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <TextField
          label="Search by name"
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          value={filter.name || ""}
          onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddModal(true)}
          sx={{
            height: 30,
            minWidth: 120,
            display: "flex",
            alignItems: "center",
            fontSize: "0.75rem",
            px: 2,
            borderRadius: 0,
            whiteSpace: "nowrap",
          }}
          startIcon={<AddIcon fontSize="small" />}
        >
          Debtor
        </Button>
      </Box>

      <InfiniteScroll
        dataLength={Partys.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          <Typography textAlign="center" p={2} color="text.secondary">
            Loading...
          </Typography>
        }
        endMessage={
          <Typography textAlign="center" p={2} color="text.disabled">
            No more debtors!
          </Typography>
        }
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {Partys.map((party, index) => (
              <MobilePartyCard key={party.id} party={party} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "88vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1900,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              {/* ================================================== */}
              {/* ✅✅✅ FIX #2: TableHead — SOLID background        */}
              {/* ================================================== */}
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    color: "text.primary",
                    backgroundColor: headerStickyBg, // ❌ pehle yaha alpha(...) tha — ab SOLID
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    letterSpacing: "0.04em",
                  },
                }}
              >
                <TableRow>
                  {/* ✅✅✅ FIX #3: S. No. header — SOLID bg + left: 0 + fixed width (70) */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      width: 70,
                      minWidth: 70,
                      bgcolor: headerStickyBg, // ❌ pehle "inherit" tha
                      zIndex: 999,
                    }}
                  >
                    S. No.
                  </TableCell>
                  {/* ✅✅✅ FIX #4: Company Name header — SOLID bg + left: 70 (S.No. ki width ke baad) */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 70,
                      zIndex: 999,
                      bgcolor: headerStickyBg, // ❌ pehle "inherit" tha
                    }}
                  >
                    Company Name
                  </TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>GST No</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone No</TableCell>
                  <TableCell>PAN</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Remarks</TableCell>
                  {/* ✅✅✅ FIX #5: Action header — SOLID bg */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      bgcolor: headerStickyBg, // ❌ pehle "inherit" tha
                      zIndex: 999,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {Partys.map((row, index) => (
                  /* ✅✅✅ FIX #6: TableRow — "hover" prop HATA DIYA + SOLID zebra + SOLID hover */
                  <TableRow
                    key={row.id || index}
                    sx={{
                      backgroundColor: zebraBg(index), // ❌ pehle alpha(...) tha
                      "&:hover": {
                        backgroundColor: rowHoverBg, // solid hover
                        "& td": { backgroundColor: "inherit !important" },
                      },
                      "& td": {
                        padding: "6px 8px",
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        color: "text.primary",
                      },
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    {/* ✅✅✅ FIX #7: S. No. body cell — left: 0 + fixed width 70 (inherit rehne do, ab row SOLID hai) */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        width: 70,
                        minWidth: 70,
                        bgcolor: "inherit",
                        zIndex: 998,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    {/* ✅✅✅ FIX #8: Company Name body cell — left: 70 (S.No. ki width ke baad) */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 70,
                        zIndex: 998,
                        bgcolor: "inherit",
                        fontWeight: 600,
                      }}
                    >
                      <Tooltip title={row.company_name} arrow>
                        <span>{truncateText(row.company_name, 20)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.gst}</TableCell>
                    <TableCell>
                      <Tooltip title={row.email} arrow>
                        <span>{truncateText(row.email, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.contact}</TableCell>
                    <TableCell>{row.pan}</TableCell>
                    <TableCell>
                      <Tooltip title={row.address} arrow>
                        <span>{truncateText(row.address, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {row.authorized_by_name || row.approved_name || "-"}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.remarks} arrow>
                        <span>{truncateText(row.remarks, 20)}</span>
                      </Tooltip>
                    </TableCell>

                    {/* Action body cell — koi change nahi, inherit theek hai ab */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: "inherit",
                        zIndex: 998,
                      }}
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleEdit(row, index)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>

                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setPartyToDelete(row);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>

                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleViewParty(row)}
                        color="info"
                        size="small"
                      >
                        <VisibilityIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* ========================================== */}
      {/* ✅ ADD PARTY DIALOG                        */}
      {/* ========================================== */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="md"
        fullWidth
        autoComplete="off"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 2,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          Add New Debtor
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* Row 1: Company Name | Contact Person | Email */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Name"
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder — shows how to fill
                placeholder={FIELD_GUIDE.company_name.demo}
                value={formData.company_name}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, company_name: value });
                  const errorMsg = validateField("company_name", value);
                  setErrors((prev) => ({ ...prev, company_name: errorMsg }));
                }}
                required
                error={!!errors.company_name}
                helperText={errors.company_name}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Person"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.name.demo}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.email.demo}
                value={formData.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  setFormData({ ...formData, email: value });
                  const errorMsg = validateField("email", value);
                  setErrors((prev) => ({ ...prev, email: errorMsg }));
                }}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            {/* Row 2: Phone | GST | PAN */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Phone No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.contact.demo}
                value={formData.contact}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9]/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  setFormData({ ...formData, contact: value });
                  const errorMsg = validateField("contact", value);
                  setErrors((prev) => ({ ...prev, contact: errorMsg }));
                }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                error={!!errors.contact}
                helperText={errors.contact}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="GST No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.gst.demo}
                value={formData.gst}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, gst: value });
                  const errorMsg = validateField("gst", value);
                  setErrors((prev) => ({ ...prev, gst: errorMsg }));
                }}
                inputProps={{ maxLength: 15 }}
                error={!!errors.gst}
                helperText={errors.gst}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="PAN No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.pan.demo}
                value={formData.pan}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, pan: value });
                  const errorMsg = validateField("pan", value);
                  setErrors((prev) => ({ ...prev, pan: errorMsg }));
                }}
                inputProps={{ maxLength: 10 }}
                error={!!errors.pan}
                helperText={errors.pan}
              />
            </Grid>
          </Grid>

          {/* ✅ CONTACT PERSONS SECTION */}
          <ContactPersonsSection
            contactPersons={formData.contact_persons}
            onAdd={handleAddContactPerson}
            onRemove={handleRemoveContactPerson}
            onChange={handleContactPersonChange}
          />

          {/* Address Field */}
          <Grid container sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Address"
                autoComplete="off"
                fullWidth
                margin="dense"
                multiline
                rows={1}
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.address.demo}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={() => {
              setOpenAddModal(false);
              setFormData(getEmptyFormData());
              resetForm();
            }}
            color="error"
            variant="outlined"
            size="small"
          >
            Close
          </Button>
          <Button
            onClick={handleAddParty}
            color="primary"
            variant="contained"
            size="small"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================== */}
      {/* VIEW PARTY DIALOG                          */}
      {/* ========================================== */}
      <Dialog
        open={openViewModal}
        onClose={() => {
          setOpenViewModal(false);
          setViewParty(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            Debtor Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {viewParty && (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "70vh",
                overflow: "auto",
                boxShadow: "none",
                bgcolor: "background.paper",
              }}
            >
              <Table size="small">
                <TableBody>
                  {[
                    { label: "Company Name", value: viewParty.company_name },
                    { label: "Contact Person", value: viewParty.name },
                    { label: "Email", value: viewParty.email },
                    { label: "Phone No", value: viewParty.contact },
                    { label: "GST No", value: viewParty.gst },
                    { label: "PAN", value: viewParty.pan },
                    { label: "State", value: viewParty.state },
                    {
                      label: "Address",
                      value: viewParty.address,
                      wrap: true,
                    },
                    {
                      label: "Status",
                      value: (
                        <Chip
                          label={viewParty.status || "Pending"}
                          color={
                            viewParty.status === "Approved"
                              ? "success"
                              : viewParty.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          variant="outlined"
                          size="small"
                        />
                      ),
                    },
                    {
                      label: "Created Date",
                      value: viewParty.created_at
                        ? formatOnlyDate(viewParty.created_at)
                        : "-",
                    },
                    {
                      label: "Approved By",
                      value:
                        viewParty.authorized_by_name || viewParty.approved_name,
                    },
                    {
                      label: "Remarks",
                      value: viewParty.remarks,
                      wrap: true,
                    },
                  ].map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: (t) =>
                          index % 2 === 0
                            ? t.palette.mode === "dark"
                              ? alpha(t.palette.common.white, 0.04)
                              : alpha(t.palette.primary.main, 0.03)
                            : "background.paper",
                        "&:hover": { bgcolor: "action.selected" },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "35%",
                          fontSize: "0.75rem",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          color: "text.primary",
                        }}
                      >
                        {item.label}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          whiteSpace: item.wrap ? "normal" : "nowrap",
                          wordBreak: item.wrap ? "break-word" : "normal",
                        }}
                      >
                        {item.value || "-"}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* ✅ Contact Persons in View Modal */}
                  {viewParty.contact_persons &&
                    viewParty.contact_persons.length > 0 && (
                      <>
                        <TableRow
                          sx={{
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.primary.main, 0.2)
                                : alpha(t.palette.primary.main, 0.08),
                          }}
                        >
                          <TableCell
                            colSpan={2}
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.8rem",
                              color: "primary.main",
                            }}
                          >
                            Contact Persons
                          </TableCell>
                        </TableRow>
                        {viewParty.contact_persons.map((cp, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              bgcolor: (t) =>
                                idx % 2 === 0
                                  ? t.palette.mode === "dark"
                                    ? alpha(t.palette.common.white, 0.02)
                                    : alpha(t.palette.primary.main, 0.02)
                                  : "background.paper",
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                borderRight: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              Person {idx + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.primary",
                              }}
                            >
                              <strong>
                                {cp.title
                                  ? `${cp.title} ${cp.contact_name}`
                                  : cp.contact_name || "-"}
                              </strong>{" "}
                              | {cp.contact_no || "-"} |{" "}
                              <em>{cp.designation || "-"}</em>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => {
              setOpenViewModal(false);
              setViewParty(null);
            }}
            color="primary"
            variant="contained"
            size="small"
            sx={{ fontSize: "0.75rem" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================== */}
      {/* ✅ EDIT PARTY DIALOG                       */}
      {/* ========================================== */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setPartyToApprove(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 2,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          Edit Debtor
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} autoComplete="off">
            {/* Row 1: Company Name | Contact Person | Email */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Company Name"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.company_name.demo}
                value={formData.company_name}
                onChange={(e) =>
                  setFormData({ ...formData, company_name: e.target.value })
                }
                required
                error={!!errors.company_name}
                helperText={errors.company_name}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Person"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.name.demo}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.email.demo}
                value={formData.email}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  setFormData({ ...formData, email: value });
                  const errorMsg = validateField("email", value);
                  setErrors((prev) => ({ ...prev, email: errorMsg }));
                }}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            {/* Row 2: Phone | GST | PAN */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Phone No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.contact.demo}
                value={formData.contact}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9]/g, "");
                  if (value.length > 10) value = value.slice(0, 10);
                  setFormData({ ...formData, contact: value });
                  const errorMsg = validateField("contact", value);
                  setErrors((prev) => ({ ...prev, contact: errorMsg }));
                }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                required
                error={!!errors.contact}
                helperText={errors.contact}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="GST No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.gst.demo}
                value={formData.gst}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, gst: value });
                  const errorMsg = validateField("gst", value);
                  setErrors((prev) => ({ ...prev, gst: errorMsg }));
                }}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="PAN No"
                fullWidth
                margin="dense"
                autoComplete="off"
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.pan.demo}
                value={formData.pan}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, pan: value });
                  const errorMsg = validateField("pan", value);
                  setErrors((prev) => ({ ...prev, pan: errorMsg }));
                }}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
          </Grid>

          {/* ✅ CONTACT PERSONS SECTION */}
          <ContactPersonsSection
            contactPersons={formData.contact_persons}
            onAdd={handleAddContactPerson}
            onRemove={handleRemoveContactPerson}
            onChange={handleContactPersonChange}
          />

          {/* Address Field */}
          <Grid container sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                autoComplete="off"
                margin="dense"
                multiline
                rows={2}
                // ✅ FIX #9: DEMO placeholder
                placeholder={FIELD_GUIDE.address.demo}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          {partyToApprove && userRole != "Staff" && (
            <>
              <Button
                color="success"
                variant="outlined"
                size="small"
                onClick={() => {
                  setActionType("Approved");
                  setOpenApproveRejectModal(true);
                }}
              >
                Approve
              </Button>

              <Button
                color="warning"
                variant="outlined"
                size="small"
                onClick={() => {
                  setActionType("Rejected");
                  setOpenApproveRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}

          <Button
            onClick={() => handleUpdateParty(formData.id)}
            color="primary"
            variant="contained"
            size="small"
          >
            Update
          </Button>

          <Button
            onClick={() => {
              setOpenEditModal(false);
              setFormData(getEmptyFormData());
              setPartyToApprove(null);
            }}
            color="error"
            variant="outlined"
            size="small"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Modal */}
      <Dialog
        open={openApproveRejectModal}
        onClose={() => {
          setOpenApproveRejectModal(false);
          setRemarkInput("");
          setActionType(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            color: actionType === "Approved" ? "success.main" : "error.main",
            fontWeight: 700,
          }}
        >
          {actionType === "Approved" ? "Approve Debtor" : "Reject Debtor"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Please enter a remark before{" "}
            {actionType === "Approved" ? "approving" : "rejecting"} this
            debtor:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            placeholder="Enter remark..."
            autoFocus
          />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={() => {
              setOpenApproveRejectModal(false);
              setRemarkInput("");
              setActionType(null);
            }}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            color={actionType === "Approved" ? "success" : "error"}
            variant="contained"
            size="small"
            onClick={handleApproveReject}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================== */}
      {/* ✅✅✅ FIX #11: VALIDATION POPUP           */}
      {/* WHAT is wrong + HOW to fix + DEMO (ENGLISH)*/}
      {/* ========================================== */}
      <Dialog
        open={validationPopup.open}
        onClose={() => setValidationPopup((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            px: 2,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.error.main, 0.15)
                : "#ffebee",
            color: "error.main",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          ⚠️ Form has {validationPopup.problems.length} error
          {validationPopup.problems.length > 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, maxHeight: "60vh" }}>
          <Typography
            sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1.5 }}
          >
            Please correct the fields below, then press{" "}
            <strong>{validationPopup.title}</strong> again:
          </Typography>

          {validationPopup.problems.map((p, i) => (
            <Box
              key={i}
              sx={{
                mb: 1.2,
                p: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#fafafa",
              }}
            >
              <Typography
                sx={{ fontSize: "0.78rem", fontWeight: 700, color: "error.main" }}
              >
                {i + 1}. {p.label}
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                ❌ Problem: {p.issue}
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: "success.main", fontWeight: 600 }}
              >
                💡 How to fix: {p.fix}
              </Typography>
              {p.demo && (
                <Typography sx={{ fontSize: "0.68rem", color: "text.disabled" }}>
                  {p.demo}
                </Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1,
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={() =>
              setValidationPopup((prev) => ({ ...prev, open: false }))
            }
            color="primary"
            variant="contained"
            size="small"
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setPartyToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.6)"
                : "0 4px 16px rgba(0,0,0,0.15)",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 220 }}>
          <Typography
            variant="body2"
            gutterBottom
            sx={{ color: "text.primary" }}
          >
            Are you sure you want to delete{" "}
            <strong>{PartyToDelete?.company_name}</strong>?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setPartyToDelete(null);
              }}
            >
              Close
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={async () => {
                await handleDelete(PartyToDelete);
                setAnchorEl(null);
                setPartyToDelete(null);
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default PartyManagementTable;
